import { useEffect, useMemo, useState } from "react"
import { useQuery } from "urql"

import type { Classification, Query } from "@/graphql/codegen/graphql"
import { ClassificationListDocument } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

const LIMIT = 200

interface UseGetProductClassOptionsReturn {
  productClassOptions: Option<string>[]
  isFetching: boolean
}

export const useGetProductClassOptions = (): UseGetProductClassOptionsReturn => {
  const [offset, setOffset] = useState(0)
  const [productClasses, setProductClasses] = useState<Classification[]>([])
  const [hasMore, setHasMore] = useState(false)

  const [{ data, fetching }] = useQuery<Pick<Query, "classification">>({
    query: ClassificationListDocument,
    variables: { input: { filter: {}, limit: LIMIT, offset } },
  })

  useEffect(() => {
    if (!data) return

    const paginatedProductClasses =
      data.classification?.list?.__typename === "ClassificationListSuccess"
        ? data.classification.list.classifications
        : []

    setProductClasses((prev) => (offset === 0 ? paginatedProductClasses : [...prev, ...paginatedProductClasses]))
    setHasMore(paginatedProductClasses.length % LIMIT === 0 && paginatedProductClasses.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (hasMore && !fetching) {
      setOffset((prev) => prev + LIMIT)
    }
  }, [hasMore, fetching])

  const productClassOptions = useMemo<Option<string>[]>(
    () =>
      productClasses
        .map((classification) => ({
          value: classification.classificationId,
          label: `${classification.categoryDetail?.name} - ${classification.name}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [productClasses]
  )

  return { productClassOptions, isFetching: fetching }
}
